"use client"

import { BLXAppIcon } from "blx-icons"
import { ScreenPaths } from "config/routing"
import { useHypertune } from "lib/feature-flags/generated/hypertune.react"
import dynamic from "next/dynamic"
import { usePathname } from "next/navigation"
import type React from "react"

import { HStack, Icon } from "@chakra-ui/react"
import { UserButton } from "@clerk/nextjs"

import { selectActivePpSubId, useAppSelector } from "services"
import OrganizationPopover from "./OrganizationPopover"
import BrewLogixLogoLink from "./components/BrewLogixLogoLink"
import DarkModeToggleButton from "./components/DarkModeToggleButton"

const OrgGroupSelectorBoxDynamic = dynamic(
    () => import("features/account/accountSelection/OrgGroupSelectorBox"),
    { ssr: false }
)

const AlertsButtonDynamic = dynamic(
    () => import("features/navigation/components/AlertsButton/AlertsButton"),
    {
        ssr: false
    }
)

const inactiveLinkPaths = [
    ScreenPaths.Dashboard,
    ScreenPaths.CustomizeOrganization,
    ScreenPaths.NewOrganization,
    ScreenPaths.ChoosePlan
]

/**
 * `ApplicationHeader` is a React functional component that renders the header of the application.
 * It includes various UI elements such as the organization popover, logo link, and user button.
 * The component's appearance and behavior can be customized through the `allowAppAccess` prop.
 *
 * @param {Object} props - The props object.
 * @param {boolean} [props.allowAppAccess=true] - Determines if the application access is allowed.
 *
 * @returns {JSX.Element} The rendered header component.
 */
const ApplicationHeader: React.FC<{
    allowAppAccess?: boolean
}> = ({ allowAppAccess = true }) => {
    const flags = useHypertune()
    const pathName = usePathname()
    const orgGroupSelectorPosition = flags.orgGroupSelectorPosition({ fallback: "bottomNav" })
    const ppSubId = useAppSelector(selectActivePpSubId)

    const linkInactive = inactiveLinkPaths.includes(pathName as ScreenPaths)

    return (
        <HStack
            zIndex={31}
            px={[4, 6, 8, 8]}
            position='fixed'
            top={0}
            h={{ base: 14, md: 16 }}
            minH={{ base: 14, md: 16 }}
            w={"100vw"}
            maxW={"100vw"}
            overflowX={"hidden"}
            borderBottomColor='gray.100'
            borderBottomWidth='1px'
            bg='white'
            _dark={{
                bg: "gray.800",
                borderBottomColor: "gray.500"
            }}
            alignItems='center'
            gap={[2, 2, 8]}
            justifyContent='space-between'
        >
            <HStack alignItems='center' gap={4} mr={[0, 0, "2.85rem"]}>
                <OrganizationPopover isDisabled={!allowAppAccess} />
                <div className='hidden md:block'>
                    <BrewLogixLogoLink linkActive={!linkInactive || !allowAppAccess} />
                </div>
                <div className='flex items-center md:hidden'>
                    <Icon as={BLXAppIcon} boxSize='30px' />
                </div>
                {orgGroupSelectorPosition === "dropDown" && <OrgGroupSelectorBoxDynamic />}
            </HStack>
            <HStack
                alignItems='center'
                zIndex={10}
                gap={[2, 5]}
                justifyContent='end'
                maxW='fit-content'
            >
                {ppSubId.length > 0 && <AlertsButtonDynamic />}
                {allowAppAccess && <DarkModeToggleButton />}

                <UserButton />
            </HStack>
        </HStack>
    )
}

export default ApplicationHeader
